import type { CSSProperties } from 'react';
import { useMediaQuery, useSeasonArt } from '../context';

// The page-wide backdrop: ground mist, embers, cobweb corners and a soft
// vignette, all behind the content. Nine embers rise along the
// page edges, staggered so only two or three are ever lit at once; the tenth
// hangs still, then sinks: the one wrong thing. Positions stay in the outer
// margins so nothing drifts behind body text. Each piece is CSS-drawn (or
// absent) until the season's raster file for it loads; then the same element
// shows the image instead.
type Ember = { x: string; dur: number; delay: number; drift: string; size: number };

const EMBERS: Ember[] = [
  { x: '1.6%', dur: 22, delay: -2, drift: '18px', size: 3 },
  { x: '5%', dur: 27, delay: -14, drift: '-12px', size: 2 },
  { x: '7%', dur: 24, delay: -20, drift: '10px', size: 2 },
  { x: '3.4%', dur: 30, delay: -8, drift: '-20px', size: 2 },
  { x: '6%', dur: 26, delay: -23, drift: '14px', size: 3 },
  { x: '97.6%', dur: 25, delay: -5, drift: '-16px', size: 3 },
  { x: '94.5%', dur: 29, delay: -17, drift: '12px', size: 2 },
  { x: '93%', dur: 23, delay: -11, drift: '-10px', size: 2 },
  { x: '96%', dur: 31, delay: -25, drift: '18px', size: 2 },
];

// A raster ember is a glow sprite, so it is drawn larger than the bare dot.
const SPRITE_SCALE = 5;

function artStyle(url: string | undefined): CSSProperties | undefined {
  return url ? { backgroundImage: `url("${url}")` } : undefined;
}

export default function HalloweenLayer() {
  // Cobwebs only show on wide windows (halloween.css), so only fetched there.
  const wide = useMediaQuery('(min-width: 1280px)');
  const art = {
    mist: useSeasonArt('mist'),
    ember: useSeasonArt('ember'),
    cobweb: useSeasonArt('cobweb', wide),
  };
  const raster = (piece: keyof typeof art) => (art[piece] ? ' is-raster' : '');

  return (
    <div className="sn-season-layer" aria-hidden="true">
      <div className="hw-vignette" />
      {art.cobweb && (
        <>
          <div className="hw-cobweb hw-cobweb-left" style={artStyle(art.cobweb)} />
          <div className="hw-cobweb hw-cobweb-right" style={artStyle(art.cobweb)} />
        </>
      )}
      <div className="hw-fog">
        <div className={`hw-fog-band hw-fog-a${raster('mist')}`} style={artStyle(art.mist)} />
        <div className={`hw-fog-band hw-fog-b${raster('mist')}`} style={artStyle(art.mist)} />
      </div>
      {EMBERS.map((e, i) => {
        const size = art.ember ? e.size * SPRITE_SCALE : e.size;
        return (
          <span
            key={i}
            className={`hw-ember${raster('ember')}`}
            style={
              {
                left: e.x,
                width: size,
                height: size,
                animationDuration: `${e.dur}s`,
                animationDelay: `${e.delay}s`,
                '--hw-drift': e.drift,
                ...artStyle(art.ember),
              } as CSSProperties
            }
          />
        );
      })}
      <span className={`hw-ember hw-ember-tenth${raster('ember')}`} style={artStyle(art.ember)} />
    </div>
  );
}
